import React from 'react';
import { connect } from '@optum-uhone-hmkts/rise';
import {
	UpsertLeadFormComponent,
	FORM_NAME,
	FormData,
} from './upsert_lead_form';
import { BasePageContainer } from '../../components/Layout/BasePage';
import { SaveCancelHeaderBarComponent } from '../../components/Layout/SaveCancelHeaderBar';
import { getFormValues } from 'redux-form';
import { v4 as uuidv4 } from 'uuid';
import { createLead, GetLead, UpdateLead } from '../../actions/lead_actions';
import { Lead } from '../../reducers/LeadReducer';
import {
	navigateBack,
} from '../../actions/navigation_actions';
import { NavigationProps } from '../../components/nav/Routes';
import _ from 'lodash';
import moment from 'moment';
import { Contact, Email, Phone } from '../../reducers/ContactReducer';
import { Policy } from '../../reducers/policy_reducer';
import {
	objToArray,
	arrayToObj,
} from '../../utilities/formatting/data_normalizations';
import { GetHousehold } from '../../actions/household_actions';
import { getPrimaryContact } from '../../selectors/contact_selectors';
import { GetPolicy, UpdatePolicy } from '../../actions/policy_actions';
import { normalizeMoneyNumeric } from '../../utilities/formatting/string_normalization';
import { ContactDedupeDialog } from '../../components/contact/contact_dedupe_dialog';
import { ContactDedupeDto } from '../../reducers/dedupe_reducer';
import { ContactDedupeCheck } from '../../actions/dedupe_actions';
import { Strings } from '../../assets/common/strings';

interface Props extends NavigationProps {
	formValues: Partial<FormData> | undefined;
	userId: string;
	impersonatingId: string;
	primaryContact: Contact;
	lead?: Lead;
	isLoading: boolean;
	policy?: Policy;
	syncErrors?: any;
	createLead: (lead: Lead, policy?: Policy) => void;
	updateLead(lead: Lead): void;
	getLead(id: string): void;
	getHousehold: (id: string) => void;
	getPolicy: (id: string) => void;
	updatePolicy: (policy: Policy) => void;
	dedupeCheck: (dto: ContactDedupeDto) => void;
}

interface State {
	isFormInvalid: boolean;
	noContactMethodSelected: boolean;
	noLineOfBusinessSelected: boolean;
	mode?: string;
	matchLeadToPolicy: boolean;
	lead? : Lead;
}

class UpsertLead extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			isFormInvalid: true,
			noContactMethodSelected: true,
			noLineOfBusinessSelected: true,
			matchLeadToPolicy: false,
		};

		this.onSave = this.onSave.bind(this);
		this.handleFormUpdate = this.handleFormUpdate.bind(this);
	}

	componentDidMount() {
		const leadID = this.props.match.params.leadID;
		const householdID = this.props.match.params.householdID;
		const policyID = this.props.match.params.policyID;

		if (policyID && policyID !== '0' && this.props.policy) {
			this.props.getPolicy(policyID);
			this.setState({
				matchLeadToPolicy: true,
			});
		}

		if (leadID == 0 && householdID != 0) {
			//this.props.getHousehold(householdID);
			this.setState({
				mode: 'newLeadExistingHousehold',
			});
		} else if (leadID != '0' && householdID != '0') {
			//this.props.getHousehold(householdID);
			if (!this.props.lead) this.props.getLead(this.props.match.params.leadID);
			this.setState({
				mode: 'editLead',
			});
		} else {
			this.setState({
				mode: 'newLeadNewHousehold',
			});
		}
	}

	componentDidUpdate(prevProps: Props, prevState: State) {
		const modeChanged = prevState.mode !== this.state.mode;
		const formValuesChanged = prevProps.formValues !== this.props.formValues;

		if (modeChanged || formValuesChanged) {
			if (this.state.mode == 'newLeadNewHousehold') {
				const fv = this.props.formValues ?? {};
				const emails = fv.emails ?? [];
				const phones = fv.phones ?? [];
				const noContactHasBeenSelected: boolean =
					emails.length + phones.length === 0;
				if (prevState.noContactMethodSelected !== noContactHasBeenSelected) {
					this.setState({
						noContactMethodSelected: noContactHasBeenSelected,
					});
				}
			} else if (prevState.noContactMethodSelected !== false) {
				this.setState({
					noContactMethodSelected: false,
				});
			}

			const linesOfBusiness = _.get(this.props, 'formValues.linesOfBusiness', {});
			const isThereASelectedLineOfBusiness: boolean =
				objToArray(linesOfBusiness).length > 0;
			const nextNoLob = !isThereASelectedLineOfBusiness;
			if (prevState.noLineOfBusinessSelected !== nextNoLob) {
				this.setState({
					noLineOfBusinessSelected: nextNoLob,
				});
			}
		}
	}

	public handleFormUpdate(
		pristine: boolean,
		invalid: boolean,
		submitting: boolean
	) {
		this.setState({
			isFormInvalid: invalid || pristine || submitting,
		});
	}

	dedupeCheck = (lead: Lead, callback?: () => void) => {
		const contact = lead.contacts[0];
		const dto = {
			phones: contact.phones ? contact.phones.map(phone => phone.number.replace(/\D/g,'')) : [],
			emails: contact.emails ? contact.emails.map(email => email.emailAddress) : [],
			callback
		}
		this.props.dedupeCheck(dto);
	}

	onCancel = () => {
		this.props.navigateBack();
	};

	onSave() {
		if (this.state.mode == 'newLeadExistingHousehold') {
			const fv = this.props.formValues ?? {};
			const contact = this.props.primaryContact;

			const householdID = this.props.match.params.householdID;

			const leadToAdd = {
				id: uuidv4(),
				userId:
					this.props.userId != this.props.impersonatingId &&
						this.props.impersonatingId
						? this.props.impersonatingId
						: this.props.userId,
				status: Strings.Status.NotContacted,
				vendor: fv.vendor,
				inputSource: fv.inputSource,
				leadType: fv.leadType,
				leadCost: Number(normalizeMoneyNumeric(String(fv.leadCost ?? ''))),
				linesOfBusiness: objToArray(fv.linesOfBusiness || {}),
				contacts: [contact],
				householdId: householdID,
				campaign: fv.campaign,
				opportunityRatingCode: fv.rating,
				updatedOn: new Date(),
			};

			this.props.createLead(leadToAdd as Lead);
			this.props.navigateBack();
		} else if (this.state.mode == 'editLead') {
			const fv = this.props.formValues ?? {};
			const leadToEdit: Lead = Object.assign({} as Lead, this.props.lead);
			leadToEdit.campaign = fv.campaign ?? leadToEdit.campaign;
			leadToEdit.leadCost = Number(
				normalizeMoneyNumeric(String(fv.leadCost ?? ''))
			);
			leadToEdit.inputSource = fv.inputSource ?? leadToEdit.inputSource;
			leadToEdit.leadType = fv.leadType ?? leadToEdit.leadType;
			leadToEdit.vendor = fv.vendor ?? leadToEdit.vendor;
			leadToEdit.opportunityRatingCode = fv.rating ?? leadToEdit.opportunityRatingCode;
			leadToEdit.linesOfBusiness = objToArray(
				fv.linesOfBusiness || {}
			);

			this.props.updateLead(leadToEdit);
			this.props.navigateBack();
		} else {
			const fv = this.props.formValues ?? {};
			let emails = (fv.emails || []).slice();
			let phones = (fv.phones || []).slice();
			const validNewEmail =
				(fv.email || '').length > 0 &&
				!_.get(this.props.syncErrors, 'email', false);
			const validNewPhone =
				(fv.phone || '').length > 0 &&
				!_.get(this.props.syncErrors, 'phone', false);
			const validNewEntry = validNewEmail || validNewPhone;

			if (validNewEntry) {
				if (validNewEmail) {
					emails = (fv.emails || []).slice();
					const email: Email = {
						emailAddress: fv.email as string,
						type: 'Home',
						isPreferred: true,
						dateCreated: moment().toDate(),
						doNotEmail: false,
					};
					if (emails.length) {
						email.isPreferred = false;
					}
					emails.push(email);
				}
				if (validNewPhone) {
					phones = (fv.phones || []).slice();
					const phone: Phone = {
						id: uuidv4(),
						number: fv.phone as string,
						type: Strings.PhoneTypes.Home,
						isPreferred: true,
						isCompanyDnc: false,
						wasCompanyDncSet: false,
						isNationalDnc: false,
						wasRpmPhoneNumberChanged: true,
						extension: '',
						dateCreated: moment().toDate(),
						isSmsDnc: false,
					};
					if (phones.length) {
						// not the first phone
						phone.isPreferred = false;
					}
					phones.push(phone);
				}
			}

			const householdID = uuidv4();
			const heightFtStr = (String(fv.heightFt ?? '')).split(' ');
			const heightInStr = (String(fv.heightIn ?? '')).split(' ');
			const heightFt = heightFtStr[0] ? parseInt(heightFtStr[0], 10) : undefined;
			const heightIn = heightInStr[0] ? parseInt(heightInStr[0], 10) : undefined;
			const contact: Partial<Contact> = {
				phones: phones,
				addresses: fv.addresses,
				emails: emails,
				wasRpmNameChanged: fv.wasRpmNameChanged || false,
				firstName: fv.firstName,
				lastName: fv.lastName,
				birthDate: fv.DOB ? moment(fv.DOB as any).toDate() : undefined,
				id: uuidv4(),
				householdId: householdID,
				householdRole: 0,
				userId:
					this.props.userId != this.props.impersonatingId &&
						this.props.impersonatingId
						? this.props.impersonatingId
						: this.props.userId,
				middleName: fv.middleName,
				isSelfEmployed: fv.isSelfEmployed,
				weightLbs: fv.weight,
				annualIncome: Number(
					normalizeMoneyNumeric(String(fv.annualIncome ?? ''))
				),
				heightIn,
				heightFt,
				isStudent: fv.isStudent,
				preferredName: fv.preferredName,
				suffix: fv.suffix,
				languagePreference: fv.languagePreference,
				isTobaccoUser: fv.isTobaccoUser,
				status: fv.status,
				salutation: fv.salutation,
				occupation: fv.occupation,
				jobTitle: fv.jobTitle,
				gender: fv.gender,
				lastFourSsn: fv.lastFourSsn,
				familyStatusCode: fv.maritalStatus,
				clientType: fv.clientType,
				lastUnemploymentYear: fv.lastUnemploymentYear,
			};

			const leadID = uuidv4();

			const leadToSend: Partial<Lead> = {
				id: leadID,
				userId:
					this.props.userId != this.props.impersonatingId &&
						this.props.impersonatingId
						? this.props.impersonatingId
						: this.props.userId,
				status: Strings.Status.NotContacted,
				vendor: fv.vendor,
				inputSource: fv.inputSource,
				leadType: fv.leadType,
				leadCost: Number(normalizeMoneyNumeric(String(fv.leadCost ?? ''))),
				linesOfBusiness: objToArray(fv.linesOfBusiness || {}),
				contacts: [contact as Contact],
				opportunityRatingCode: fv.rating,
				campaign: fv.campaign,
			};

			if (this.state.matchLeadToPolicy && this.props.policy) {
				const updatedPolicy = this.props.policy;
				updatedPolicy.primaryInsured = contact as Contact;
				this.setState(
					{ lead: leadToSend as Lead },
					() => this.dedupeCheck(leadToSend as Lead)
				);
			} else {
				this.setState(
					{ lead: leadToSend as Lead },
					() => this.dedupeCheck(leadToSend as Lead)
				);
			}
		}
	}

	createInitalValues() {
		let initialValues = {
			rating: 'Warm',
			inputSource: 'Manual',
		} as Partial<FormData>;
		if (this.state.mode == 'editLead' && this.props.lead) {
			initialValues = {
				campaign: this.props.lead.campaign,
				leadType: this.props.lead.leadType,
				leadCost: `${this.props.lead.leadCost}`,
				vendor: this.props.lead.vendor,
				rating: this.props.lead.opportunityRatingCode,
				linesOfBusiness: arrayToObj(this.props.lead.linesOfBusiness),
				inputSource: this.props.lead.inputSource,
			};
		}
		return initialValues;
	}

	render() {
		const { lead } = this.props;
		const hasFund = Boolean(lead && lead.fund);
		const inputSource = lead && lead.inputSource ? lead.inputSource : 'none';

		return (
			<BasePageContainer
				topComponent={
					<SaveCancelHeaderBarComponent
						isSaveDisabled={
							this.state.isFormInvalid ||
							this.state.noContactMethodSelected ||
							this.state.noLineOfBusinessSelected ||
							this.props.isLoading
						}
						isLoading={this.props.isLoading}
						onSave={this.onSave}
						onCancel={this.onCancel}
						title={this.state.mode == 'editLead' ? 'Edit Lead' : 'New Lead'}
						subtitle={'Household'}
						saveText="Save"
					/>
				}
			>
				<ContactDedupeDialog
					lead={this.state.lead}
					policy={this.props.policy}
					history={this.props.history}
				/>
				<UpsertLeadFormComponent
					mode={this.state.mode}
					onUpdate={this.handleFormUpdate}
					initialValues={this.createInitalValues()}
					hasFund={hasFund}
					inputSource={inputSource}
				/>
			</BasePageContainer>
		);
	}
}

function mapStateToProps(
	state,
	ownProps: Props
): Partial<Props> {
	
	const leadIndex = state.lead.leads.findIndex(loadedLead =>
			ownProps.match.params.leadID &&
			loadedLead.data.id == ownProps.match.params.leadID
	);
	const loadedLead = leadIndex == -1 ? undefined : state.lead.leads[leadIndex];
	const policyIndex = state.policy.policies.findIndex(
		loadedPolicy =>
			ownProps.match.params.policyID &&
			loadedPolicy.data.id == ownProps.match.params.policyID
	);
	const policy =
		policyIndex === -1 ? undefined : state.policy.policies[policyIndex].data;

	return {
		lead: loadedLead ? loadedLead.data : undefined,
		isLoading: state.lead.isLoading || state.dedupe.loading,
		policy,
		formValues: getFormValues(FORM_NAME)(state) as Partial<FormData> | undefined,
		syncErrors: _.get(state, `form.${FORM_NAME}.syncErrors`),
		userId: state.user.id,
		impersonatingId: state.user.impersonatingId,
		primaryContact: getPrimaryContact(state, ownProps),
	};
}

function mapDispatchToProps(dispatch: any, ownProps: Props): Partial<Props> {
	return {
		createLead: (lead: Lead, policy?: Policy) =>
			dispatch(createLead(lead, policy, ownProps.history)),
		navigateBack: () => dispatch(navigateBack()),
		getHousehold: (id: string) => dispatch(GetHousehold.started(id)),
		getLead: (id: string) => dispatch(GetLead.started(id)),
		updateLead: (lead: Lead) => dispatch(UpdateLead.started(lead)),
		getPolicy: (policyId: string) => dispatch(GetPolicy.started(policyId)),
		updatePolicy: (policy: Policy) => dispatch(UpdatePolicy.started(policy)),
		dedupeCheck: (dto: ContactDedupeDto) => dispatch(ContactDedupeCheck.started(dto))
	};
}

export const UpsertLeadContainer = connect(mapStateToProps, mapDispatchToProps, true)(
	UpsertLead
);
